import React from 'react';
import { Dialog } from 'primereact/dialog';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';

interface StudyMaterialFile {
  file_name: string;
  key: string;
  url: string;
  size: number;
  mime_type: string;
  uploaded_at: string;
}

interface StudyMaterial {
  id: string;
  chapter_id?: string;
  module_id?: string;
  course_id: string;
  institute_id?: string;
  title: string;
  body: string;
  status: string;
  is_course_module_active?: boolean;
  files: StudyMaterialFile[];
  video_links: string[];
  created_at: string;
  created_by?: string;
}

interface ViewStudyMaterialModalProps {
  visible: boolean;
  material: StudyMaterial | null;
  onHide: () => void;
}

const ViewStudyMaterialModal: React.FC<ViewStudyMaterialModalProps> = ({
  visible,
  material,
  onHide,
}) => {
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (mimeType: string): string => {
    if (mimeType.includes('pdf')) return 'pi pi-file-pdf';
    if (mimeType.includes('word')) return 'pi pi-file-word';
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'pi pi-file-excel';
    if (mimeType.includes('image')) return 'pi pi-image';
    return 'pi pi-file';
  };

  if (!material) return null;

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      style={{ width: '90vw', maxWidth: '800px' }}
      dismissableMask
      draggable={false}
      pt={{
        root: {
          style: {
            borderRadius: '20px',
            overflow: 'hidden',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          },
        },
      }}
    >
      <div>
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 50%, #0a2f2f 100%)',
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '24px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute',
            top: '-50px',
            right: '-50px',
            width: '150px',
            height: '150px',
            background: 'rgba(212, 241, 126, 0.1)',
            borderRadius: '50%',
            filter: 'blur(40px)'
          }} />
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{
              display: 'inline-block',
              padding: '4px 10px',
              background: 'rgba(212, 241, 126, 0.2)',
              borderRadius: '16px',
              marginBottom: '8px',
              fontSize: '0.65rem',
              fontWeight: '600',
              letterSpacing: '0.5px',
              textTransform: 'uppercase'
            }}>
              Study Material {material.is_course_module_active && (
                <span style={{ marginLeft: '6px', color: '#d4f17e' }}>• Course Module</span>
              )}
            </div>
            <h2 style={{ margin: '0 0 6px 0', fontSize: '1.4rem', fontWeight: '700' }}>
              {material.title}
            </h2>
            <p style={{ margin: '0', color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem' }}>
              <i className="pi pi-calendar" style={{ marginRight: '6px' }}></i>
              {formatDate(material.created_at)}
            </p>
          </div>
        </div>

        {/* Content */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '20px'
        }}>
          <div style={{
            fontSize: '0.95rem',
            color: '#333',
            lineHeight: '1.6',
            whiteSpace: 'pre-wrap'
          }}>
            {material.body}
          </div>
        </div>

        {/* Files */}
        {material.files.length > 0 && (
          <div style={{
            background: '#f8f9fa',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '20px',
            border: '1px solid #e9ecef'
          }}>
            <h3 style={{
              margin: '0 0 16px 0',
              fontSize: '0.9rem',
              fontWeight: '600',
              color: '#0f3d3e'
            }}>
              <i className="pi pi-paperclip" style={{ marginRight: '8px' }}></i>
              Attachments ({material.files.length})
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {material.files.map((file, index) => (
                <div
                  key={index}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px',
                    background: 'white',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                    <i className={getFileIcon(file.mime_type)} style={{ fontSize: '1.4rem', color: '#0f3d3e' }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: '0.85rem',
                        fontWeight: '500',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {file.file_name}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#666' }}>
                        {formatFileSize(file.size)}
                      </div>
                    </div>
                  </div>
                  <a
                    href={file.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      padding: '6px 12px',
                      background: '#0f3d3e',
                      color: 'white',
                      textDecoration: 'none',
                      borderRadius: '6px',
                      fontSize: '0.8rem',
                      fontWeight: '600'
                    }}
                  >
                    <i className="pi pi-download" style={{ marginRight: '4px' }}></i>
                    Download
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Video Links */}
        {material.video_links.length > 0 && (
          <div style={{
            background: '#f8f9fa',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '20px',
            border: '1px solid #e9ecef'
          }}>
            <h3 style={{
              margin: '0 0 16px 0',
              fontSize: '0.9rem',
              fontWeight: '600',
              color: '#0f3d3e'
            }}>
              <i className="pi pi-video" style={{ marginRight: '8px' }}></i>
              Video Links ({material.video_links.length})
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {material.video_links.map((link, index) => (
                <a
                  key={index}
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    padding: '12px',
                    background: 'white',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    color: '#0f3d3e',
                    textDecoration: 'none',
                    fontSize: '0.85rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f5f5f5';
                    e.currentTarget.style.borderColor = '#0f3d3e';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'white';
                    e.currentTarget.style.borderColor = '#e0e0e0';
                  }}
                >
                  <i className="pi pi-external-link"></i>
                  <span style={{
                    flex: 1,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {link}
                  </span>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <GlobalButton
            label="Close"
            variant="outline"
            onClick={onHide}
          />
        </div>
      </div>
    </Dialog>
  );
};

export default ViewStudyMaterialModal;